import { put, takeEvery, call, all } from "redux-saga/effects";
import { toast } from "react-toastify";

import { types } from "../../Types/agent";
import {
  fetchAgent,
  saveAgent,
  saveAgentExample,
  runAgentTest,
  fetchInboxMessages,
  fetchDrafts,
  fetchDraftStats,
  requestDraftGeneration,
  patchDraft,
  postDraftDecision,
  postDraftSend,
  startMailboxWatch,
  stopMailboxWatch,
  fetchAvatars,
  postAvatar,
  fetchAvatar,
  postAvatarConsent,
  postDefaultAvatar,
  removeAvatar,
  postDraftVideo,
} from "./api";

type SagaGenerator<T = any> = Generator<any, T, any>;

const errorMessage = (error: any, fallback: string) => {
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.message) return error.message;
  return fallback;
};

function* getAgentSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchAgent);
    yield put({ type: types.GET_AGENT_SUCCESS, payload: result.agent });
  } catch (error: any) {
    yield put({ type: types.GET_AGENT_FAILURE, payload: errorMessage(error, "Failed to load agent") });
  }
}

function* updateAgentSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(saveAgent, action.payload);
    yield put({ type: types.UPDATE_AGENT_SUCCESS, payload: result.agent });
    toast.info("Agent saved");
  } catch (error: any) {
    yield put({ type: types.UPDATE_AGENT_FAILURE });
    toast.error(errorMessage(error, "Failed to save agent"));
  }
}

function* addAgentExampleSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(saveAgentExample, action.payload);
    yield put({ type: types.ADD_AGENT_EXAMPLE_SUCCESS, payload: result.agent });
    toast.info("Example added");
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to add example"));
  }
}

function* testAgentSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(runAgentTest, action.payload);
    yield put({ type: types.TEST_AGENT_SUCCESS, payload: result });
  } catch (error: any) {
    const message = errorMessage(error, "Agent test failed");
    yield put({ type: types.TEST_AGENT_FAILURE, payload: message });
    toast.error(message);
  }
}

function* getInboxMessagesSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchInboxMessages);
    yield put({ type: types.GET_INBOX_MESSAGES_SUCCESS, payload: result.messages });
  } catch (error: any) {
    yield put({ type: types.GET_INBOX_MESSAGES_FAILURE });
    toast.error(errorMessage(error, "Failed to load inbox"));
  }
}

function* getDraftsSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(fetchDrafts, action.payload);
    yield put({ type: types.GET_DRAFTS_SUCCESS, payload: result.drafts });
  } catch (error: any) {
    yield put({ type: types.GET_DRAFTS_FAILURE });
  }
}

function* getDraftStatsSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchDraftStats);
    yield put({ type: types.GET_DRAFT_STATS_SUCCESS, payload: result });
  } catch (error: any) {
    // stats are informational only
  }
}

function* generateDraftSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(requestDraftGeneration, action.payload);
    yield put({ type: types.GENERATE_DRAFT_SUCCESS, payload: result.draft });
    if (result.draft?.decision === "reply") {
      toast.info("Draft ready for review");
    } else {
      toast.info(`Agent decided not to reply (${result.draft?.reason || result.draft?.decision})`);
    }
    yield put({ type: types.GET_INBOX_MESSAGES });
    yield put({ type: types.GET_DRAFT_STATS });
  } catch (error: any) {
    yield put({ type: types.GENERATE_DRAFT_FAILURE });
    toast.error(errorMessage(error, "Failed to generate a draft"));
  }
}

function* updateDraftBodySaga(action: any): SagaGenerator<void> {
  const { id, body } = action.payload;
  try {
    const result = yield call(patchDraft, id, body);
    yield put({ type: types.DECIDE_DRAFT_SUCCESS, payload: result.draft });
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to save the draft"));
  }
}

function* decideDraftSaga(action: any): SagaGenerator<void> {
  const { id, action: decision, body } = action.payload;
  try {
    const result = yield call(postDraftDecision, id, decision, body);
    yield put({ type: types.DECIDE_DRAFT_SUCCESS, payload: result.draft });
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to update the draft"));
  }
}

function* sendDraftSaga(action: any): SagaGenerator<void> {
  const { id } = action.payload;
  try {
    const result = yield call(postDraftSend, id);
    yield put({ type: types.SEND_DRAFT_SUCCESS, payload: result.draft.id });
    yield put({ type: types.DECIDE_DRAFT_SUCCESS, payload: result.draft });
    toast.success(result.alreadySent ? "Already sent" : "Reply sent");
    yield put({ type: types.GET_DRAFT_STATS });
    yield put({ type: types.GET_INBOX_MESSAGES });
  } catch (error: any) {
    yield put({ type: types.SEND_DRAFT_FAILURE });
    toast.error(errorMessage(error, "Failed to send the reply"));
  }
}

function* startWatchSaga(): SagaGenerator<void> {
  try {
    const result = yield call(startMailboxWatch);
    yield put({ type: types.START_WATCH_SUCCESS, payload: result });
    toast.success("Live inbox sync enabled");
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to enable inbox sync. Reconnect Gmail first."));
  }
}

function* stopWatchSaga(): SagaGenerator<void> {
  try {
    yield call(stopMailboxWatch);
    toast.info("Live inbox sync disabled");
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to disable inbox sync"));
  }
}

function* getAvatarsSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchAvatars);
    yield put({ type: types.GET_AVATARS_SUCCESS, payload: result.avatars });
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to load avatars"));
  }
}

function* createAvatarSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(postAvatar, action.payload);
    yield put({ type: types.CREATE_AVATAR_SUCCESS, payload: result.avatar });
    toast.success("Avatar creation started");
  } catch (error: any) {
    yield put({ type: types.CREATE_AVATAR_FAILURE });
    toast.error(errorMessage(error, "Failed to create avatar"));
  }
}

function* syncAvatarSaga(action: any): SagaGenerator<void> {
  const { id } = action.payload;
  try {
    const result = yield call(fetchAvatar, id);
    yield put({ type: types.SYNC_AVATAR_SUCCESS, payload: result.avatar });
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to refresh avatar"));
  }
}

function* requestConsentSaga(action: any): SagaGenerator<void> {
  const { id } = action.payload;
  try {
    const result = yield call(postAvatarConsent, id);
    yield put({ type: types.REQUEST_AVATAR_CONSENT_SUCCESS, payload: result.avatar });
    if (result.avatar?.consentUrl) {
      toast.info("Consent link ready. Send it to the person on camera.");
    }
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to create the consent link"));
  }
}

function* setDefaultAvatarSaga(action: any): SagaGenerator<void> {
  const { id } = action.payload;
  try {
    const result = yield call(postDefaultAvatar, id);
    yield put({ type: types.SET_DEFAULT_AVATAR_SUCCESS, payload: result.avatar });
    toast.success("Default avatar updated");
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to set the default avatar"));
  }
}

function* deleteAvatarSaga(action: any): SagaGenerator<void> {
  const { id } = action.payload;
  try {
    yield call(removeAvatar, id);
    yield put({ type: types.DELETE_AVATAR_SUCCESS, payload: id });
    toast.info("Avatar removed");
  } catch (error: any) {
    toast.error(errorMessage(error, "Failed to remove the avatar"));
  }
}

function* createDraftVideoSaga(action: any): SagaGenerator<void> {
  const { id, ...payload } = action.payload;
  try {
    const result = yield call(postDraftVideo, id, payload);
    yield put({ type: types.CREATE_DRAFT_VIDEO_SUCCESS, payload: result.draft });
    toast.success("Video generation started");
    yield put({ type: types.GET_DRAFTS });
    yield put({ type: types.GET_INBOX_MESSAGES });
  } catch (error: any) {
    yield put({ type: types.CREATE_DRAFT_VIDEO_FAILURE });
    toast.error(errorMessage(error, "Failed to start video generation"));
  }
}

export function* agentWatcher(): SagaGenerator<void> {
  yield all([
    takeEvery(types.GET_AGENT, getAgentSaga),
    takeEvery(types.UPDATE_AGENT, updateAgentSaga),
    takeEvery(types.ADD_AGENT_EXAMPLE, addAgentExampleSaga),
    takeEvery(types.TEST_AGENT, testAgentSaga),
    takeEvery(types.GET_INBOX_MESSAGES, getInboxMessagesSaga),
    takeEvery(types.GET_DRAFTS, getDraftsSaga),
    takeEvery(types.GET_DRAFT_STATS, getDraftStatsSaga),
    takeEvery(types.GENERATE_DRAFT, generateDraftSaga),
    takeEvery(types.UPDATE_DRAFT_BODY, updateDraftBodySaga),
    takeEvery(types.DECIDE_DRAFT, decideDraftSaga),
    takeEvery(types.SEND_DRAFT, sendDraftSaga),
    takeEvery(types.START_WATCH, startWatchSaga),
    takeEvery(types.STOP_WATCH, stopWatchSaga),
    takeEvery(types.GET_AVATARS, getAvatarsSaga),
    takeEvery(types.CREATE_AVATAR, createAvatarSaga),
    takeEvery(types.SYNC_AVATAR, syncAvatarSaga),
    takeEvery(types.REQUEST_AVATAR_CONSENT, requestConsentSaga),
    takeEvery(types.SET_DEFAULT_AVATAR, setDefaultAvatarSaga),
    takeEvery(types.DELETE_AVATAR, deleteAvatarSaga),
    takeEvery(types.CREATE_DRAFT_VIDEO, createDraftVideoSaga),
  ]);
}
